import { THUMB_KANTE, THUMB_MAX_BYTES, formatFuer } from '@fh/shared/media'

/**
 * Bildaufbereitung auf dem Gerät (ARCHITECTURE.md §11).
 *
 * Zwei Aufgaben, beide gehören hierher und nicht auf den Server:
 *
 *  1. **EXIF entfernen.** Ein Foto vom Handy trägt GPS-Koordinaten,
 *     Aufnahmezeit und Gerätemodell mit sich. Wer ein Urlaubsbild in den
 *     Familienchat stellt, teilt sonst ungefragt seine Wohnadresse mit —
 *     jedes Foto vom Balkon ist eine Standortangabe.
 *  2. **Vorschaubild erzeugen.** In Zone A KANN der Server das nicht: Er sieht
 *     ein verschlüsseltes Paket. Damit die Galerie trotzdem etwas anzuzeigen
 *     hat, entsteht die Vorschau hier und wird mitverschlüsselt.
 *
 * Der Trick beim EXIF-Entfernen ist, dass es keiner ist: Ein Bild durch
 * `canvas` neu zu kodieren überträgt nur Pixel. Alles andere — GPS, Zeit,
 * Kamera, Bearbeitungsverlauf — bleibt zwangsläufig zurück. Eine Bibliothek,
 * die EXIF-Felder einzeln herausschneidet, müsste dagegen jedes Feld kennen,
 * das es gibt, und würde beim nächsten Format-Update lückenhaft.
 */

export interface AufbereitetesBild {
  /** Die Datei, wie sie hochgeladen wird — ohne EXIF, falls entfernt wurde. */
  daten: Blob
  /** Vorschaubild, immer JPEG und immer klein. */
  vorschau: Blob | null
  breite: number
  hoehe: number
  /** Wurden tatsächlich Metadaten entfernt? Für die ehrliche Anzeige. */
  exifEntfernt: boolean
}

/**
 * Bereitet ein Bild vor.
 *
 * `ortBehalten` ist der sichtbare Schalter aus der Anforderung („Ort im Bild
 * behalten?"), Voreinstellung aus. Er ist bewusst kein verstecktes Häkchen in
 * den Einstellungen: Die Entscheidung fällt beim Senden, weil sie von diesem
 * einen Bild abhängt.
 */
export async function bildAufbereiten(
  datei: File,
  { ortBehalten = false } = {},
): Promise<AufbereitetesBild> {
  const format = formatFuer(datei.type)

  // Kein Bild oder ein Format ohne Vorschau (Video, PDF): unverändert lassen.
  if (!format?.vorschau) {
    return { daten: datei, vorschau: null, breite: 0, hoehe: 0, exifEntfernt: false }
  }

  const bild = await laden(datei)
  try {
    const vorschau = await zeichnen(bild, THUMB_KANTE, 0.7)

    // Ohne EXIF-Wunsch wird das Original ebenfalls neu kodiert — dabei fallen
    // die Metadaten weg. Mit Wunsch bleibt die Datei, wie sie ist.
    const daten = ortBehalten
      ? datei
      : await zeichnen(bild, Math.max(bild.width, bild.height), 0.92)

    return {
      daten,
      vorschau: vorschau.size <= THUMB_MAX_BYTES ? vorschau : await zeichnen(bild, 200, 0.6),
      breite: bild.width,
      hoehe: bild.height,
      exifEntfernt: !ortBehalten && format.exif,
    }
  } finally {
    bild.close()
  }
}

async function laden(datei: File): Promise<ImageBitmap> {
  // `createImageBitmap` dekodiert außerhalb des Hauptthreads. Auf einem alten
  // Telefon ist das der Unterschied zwischen einer kurzen Pause und einer
  // Sekunde, in der die Oberfläche einfriert (Anf. 76).
  return createImageBitmap(datei)
}

async function zeichnen(bild: ImageBitmap, maxKante: number, qualitaet: number): Promise<Blob> {
  const faktor = Math.min(1, maxKante / Math.max(bild.width, bild.height))
  const breite = Math.max(1, Math.round(bild.width * faktor))
  const hoehe = Math.max(1, Math.round(bild.height * faktor))

  const canvas = new OffscreenCanvas(breite, hoehe)
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('Kein 2D-Kontext verfügbar')
  ctx.drawImage(bild, 0, 0, breite, hoehe)

  return canvas.convertToBlob({ type: 'image/jpeg', quality: qualitaet })
}

/** Ob dieser Browser die Aufbereitung überhaupt kann. */
export function kannAufbereiten(): boolean {
  return typeof OffscreenCanvas !== 'undefined' && typeof createImageBitmap === 'function'
}
